import {AssistantArticle, AssistantSearchContext} from "./AssistantKnowledge.service";

type DeepSeekChatResponse = {
  choices?: Array<{
    message?: {
      content?: string | null;
    };
  }>;
  error?: {
    message?: string;
  };
};

export type DeepSeekAssistantAnswer = {
  answer: string;
  articleId: string | null;
};

export type DeepSeekConversationMessage = {
  role: "user" | "assistant";
  content: string;
};

const apiKey = process.env.REACT_APP_DEEPSEEK_API_KEY?.trim();
const model = process.env.REACT_APP_DEEPSEEK_MODEL?.trim() || "deepseek-flash";
const endpoint = "https://api.deepseek.com/chat/completions";

export const isDeepSeekConfigured = Boolean(apiKey && apiKey !== "replace_with_your_key");

const buildKnowledgeContext = (articles: AssistantArticle[]) => articles.map((article) => ({
  id: article.id,
  title: article.title,
  questions: article.questions,
  keywords: article.keywords,
  section: article.route.label,
  steps: article.content
}));

export const askDeepSeekAssistant = async (
  question: string,
  articles: AssistantArticle[],
  context: AssistantSearchContext,
  conversationHistory: DeepSeekConversationMessage[] = []
): Promise<DeepSeekAssistantAnswer> => {
  if (!isDeepSeekConfigured || !apiKey) {
    throw new Error("DeepSeek API key is not configured");
  }

  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${apiKey}`
    },
    body: JSON.stringify({
      model,
      messages: [
        {
          role: "system",
          content: [
            "Ты — помощник пользователя сайта управления часовыми станциями BigClock.",
            "Отвечай на русском языке, кратко, понятно и доброжелательно.",
            "Используй только приложенную базу знаний. Не придумывай разделы, кнопки и действия.",
            "Учитывай историю разговора и разрешай ссылки пользователя вроде «это», «там» и «а как сохранить?» по предыдущим сообщениям.",
            "Если подходящей инструкции нет, честно сообщи об этом.",
            "Верни только JSON вида: {\"answer\":\"текст ответа\",\"articleId\":\"id инструкции или null\"}.",
            "В answer перечисли необходимые шаги. Не используй Markdown-заголовки."
          ].join(" ")
        },
        ...conversationHistory,
        {
          role: "user",
          content: JSON.stringify({
            question,
            pageContext: context,
            knowledgeBase: buildKnowledgeContext(articles)
          })
        }
      ],
      response_format: {type: "json_object"},
      max_tokens: 700,
      stream: false
    })
  });

  const payload = await response.json() as DeepSeekChatResponse;
  if (!response.ok) {
    throw new Error(payload.error?.message || `DeepSeek request failed: ${response.status}`);
  }

  const content = payload.choices?.[0]?.message?.content;
  if (!content) {
    throw new Error("DeepSeek returned an empty response");
  }

  const parsed = JSON.parse(content) as Partial<DeepSeekAssistantAnswer>;
  if (!parsed.answer || typeof parsed.answer !== "string") {
    throw new Error("DeepSeek returned an invalid response");
  }

  return {
    answer: parsed.answer.trim(),
    articleId: typeof parsed.articleId === "string" ? parsed.articleId : null
  };
};
